import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type CoachingViewResult } from '@whinerp/client';
import { formatCount, formatDecimal, formatMoney, formatPercent } from '@whinerp/contracts/format';
import { ErrorState, PageHeading, Panel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { ActivityOutcomesPanel } from './activity-outcomes-panel.js';

/**
 * SC-72 Coaching view (F-103): each rep's activity and outcomes beside the
 * team median, with an eight-week trend. Worded for a coaching conversation -
 * "compared with the team", "worth talking about" - never as monitoring, and
 * never ranked.
 */

const TYPES = ['call', 'email', 'meeting', 'task'] as const;
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

function Sparkline({ values }: { values: number[] }): JSX.Element {
  const max = Math.max(1, ...values);
  const points = values.map((value, index) => `${String(index * 10)},${String(20 - (value / max) * 18)}`).join(' ');
  return (
    <svg viewBox="0 0 70 20" className="h-5 w-16" aria-hidden>
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze" />
    </svg>
  );
}

function CoachingScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const [days, setDays] = useState(30);
  const data = useQuery<CoachingViewResult, ApiError>({
    queryKey: ['coaching', days],
    queryFn: () => api.coachingView({ query: { days } }),
    placeholderData: (previous) => previous,
  });
  if (data.isPending) return <Skeleton className="h-plot w-full" />;
  if (data.isError) return <ErrorState title={t('state.error')} body={errorText(data.error)} onRetry={() => void data.refetch()} />;
  const view = data.data;
  const vsMedian = (value: number, median: number) =>
    median === 0 ? '' : value >= median ? t('coaching.atOrAbove') : t('coaching.worthTalking');
  return (
    <div data-component="Coaching" className="flex w-full flex-col gap-4">
      <PageHeading>{t('coaching.title')}</PageHeading>
      <p className="text-body text-ink-meta">{t('coaching.help')}</p>
      <select data-coaching-days aria-label={t('coaching.period')} className={`${INPUT} self-start`} value={days} onChange={(e) => setDays(Number(e.target.value))}>
        {[30, 90, 180].map((option) => (
          <option key={option} value={option}>{t('coaching.lastDays', { values: { days: String(option) } })}</option>
        ))}
      </select>
      <Panel>
        {view.rows.length === 0 ? (
          <p data-coaching-empty className="text-body text-ink-meta">{t('coaching.none')}</p>
        ) : (
          <div data-scroll-container className="overflow-x-auto">
            <table data-coaching-grid className="w-full text-dense">
              <thead>
                <tr className="text-meta text-ink-meta">
                  <th className="p-1 text-start font-normal">{t('coaching.person')}</th>
                  {TYPES.map((type) => (
                    <th key={type} className="p-1 text-end font-normal">{t(`coaching.type.${type}`)}</th>
                  ))}
                  <th className="p-1 text-end font-normal">{t('coaching.won')}</th>
                  <th className="p-1 text-end font-normal">{t('coaching.winRate')}</th>
                  <th className="p-1 text-end font-normal">{t('coaching.wonValue')}</th>
                  <th className="p-1 text-end font-normal">{t('coaching.trend')}</th>
                </tr>
              </thead>
              <tbody>
                <tr data-coaching-median className="border-b border-b-rule text-ink-meta">
                  <td className="p-1">{t('coaching.teamMedian')}</td>
                  {TYPES.map((type) => (
                    <td key={type} className="p-1 text-end">{formatDecimal(String(view.median.activity[type]), format)}</td>
                  ))}
                  <td className="p-1 text-end">{formatDecimal(String(view.median.won), format)}</td>
                  <td className="p-1 text-end">{view.median.winRate === null ? '-' : formatPercent(view.median.winRate, format)}</td>
                  <td className="p-1" />
                  <td className="p-1" />
                </tr>
                {view.rows.map((row) => (
                  <tr key={row.userId} data-coaching-row={row.name}>
                    <td className="p-1 text-ink">{row.name}</td>
                    {TYPES.map((type) => (
                      <td key={type} className="p-1 text-end text-ink" title={vsMedian(row.activity[type], view.median.activity[type])}>
                        {formatCount(row.activity[type], format)}
                      </td>
                    ))}
                    <td className="p-1 text-end text-ink">{formatCount(row.won, format)}</td>
                    <td className="p-1 text-end text-ink">{row.winRate === null ? '-' : formatPercent(row.winRate, format)}</td>
                    <td className="p-1 text-end text-ink">{formatMoney(row.wonValue, format, { currency: view.currency })}</td>
                    <td className="p-1 text-end">
                      <Sparkline values={row.weekly} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <ActivityOutcomesPanel />
    </div>
  );
}

function Loading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function Failed(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('coaching.none')} />;
}

export const screen = defineScreen({
  id: 'SC-72',
  route: '/analytics/coaching',
  surface: 'page',
  title: 'Coaching view',
  features: ['F-103'],
  entitlement: 'F-103',
  reachedBy: [{ kind: 'screen', from: 'SC-53', how: 'Coaching view link on the reports list' }],
  components: [],
  elements: ['Coaching'],
  states: { empty: Loading, loading: Loading, error: Failed },
  component: CoachingScreen,
});

export default screen;
